// Copyright (c) Docugami, Inc. All rights reserved.

import { FileStatusCategory } from 'filemanager/FileStatus';
import { DocumentStatus } from 'networking/api';
import { useState } from 'react';
import { GetDocumentsRequestFilters, SortableDocumentField } from 'store/frontend/frontendSlice';

export interface IDocumentsFilters {
    filters: GetDocumentsRequestFilters;
    statuses: FileStatusCategory[];
    addStatus: (status: FileStatusCategory, value: boolean) => void,
    setStatuses: (statuses: Record<keyof typeof FileStatusCategory, boolean>) => void,
    setFilename: (name: string) => void;
    clearFilters: () => void;
    setSortState: (sortState: SortState | undefined) => void;
}

type SortState = { field: SortableDocumentField, descending?: boolean };

// Hook to create and control the GetDocuments request for getting docset documents with filters
export const useGetDocumentsFilters = (): IDocumentsFilters => {
    const [filters, setFilters] = useState<GetDocumentsRequestFilters>({});

    function addStatus(status: FileStatusCategory, value: boolean) {
        const documentStatus = getDocumentStatusForFileStatus(status);
        setFilters(prev => {
            const request = { ...prev };
            const statusSet = new Set(request.status);
            if (value) {
                statusSet.add(documentStatus);
            } else {
                statusSet.delete(documentStatus);
            }
            request.status = Array.from(statusSet);
            return request;
        });
    }

    function setStatuses(statuses: Record<keyof typeof FileStatusCategory, boolean>) {
        const documentStatuses = new Set<string>();
        for (const status in statuses) {
            if (statuses[status as FileStatusCategory]) {
                documentStatuses.add(getDocumentStatusForFileStatus(status as FileStatusCategory));
            }
        }
        setFilters(prev => ({ ...prev, status: getArrayOrDefault(Array.from(documentStatuses)) }));
    }

    function setFilename(name: string) {
        setFilters(v => ({ ...v, name: getStringOrDefault(name.toLocaleLowerCase()) }));
    }

    function setSortState(sortState: SortState | undefined) {
        setFilters(prev => ({
            ...prev,
            sortField: sortState?.field,
            sortOrder: !sortState?.field ? undefined : sortState.descending ? "desc" : "asc"
        }));
    }

    return {
        filters,
        statuses: Object.keys(FileStatusCategory) as FileStatusCategory[],
        addStatus: addStatus,
        setStatuses,
        setFilename,
        clearFilters: () => setFilters({}),
        setSortState
    };
};

// Helpers to get default request parameter values
const getArrayOrDefault = (arr: any[]) => !!arr.length ? arr : undefined;
const getStringOrDefault = (str?: string) => !!str ? str : undefined;

// Helper function that gets the appropriate document status from UI file status
function getDocumentStatusForFileStatus(category: FileStatusCategory): DocumentStatus {
    switch (category) {
        case FileStatusCategory.Ready:
            return DocumentStatus.Ready
        case FileStatusCategory.Preparing:
            return DocumentStatus.Ingesting
        case FileStatusCategory.Processing:
            return DocumentStatus.Processing;
        case FileStatusCategory.NeedsAttention:
        case FileStatusCategory.Pending:
            return DocumentStatus.Ingested;
        case FileStatusCategory.Error:
            return DocumentStatus.Error
        case FileStatusCategory.OverQuota:
            return DocumentStatus.OverQuota;
        default:
            throw new Error(`Unsupported file status category: ${category}`);
    }
}